/**
 * Global Property Relationship Graph Component
 * 
 * Interactive graph visualization showing relationships between global properties
 * and connections to option catalogs. Uses D3.js for rendering.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import {
  Card,
  Spin,
  Space,
  Button,
  Select,
  Input,
  Typography,
  Tag,
  message,
  Modal
} from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  SearchOutlined,
  ClearOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import {
  PropertyDefinition,
} from '../../types/globalProperties';
import { dependencyKeysFromCalculationDependencies } from '../../utils/calculationDependencyKeys';
import { GlobalPropertyDetailsModal } from './GlobalPropertyDetailsModal';
import styles from './GlobalPropertyRelationshipGraph.module.css';

const { Option } = Select;
const { Search } = Input;
const { Title, Text } = Typography;

interface GraphNode {
  id: string;
  propertyKey: string;
  propertyType: string;
  nodeType: 'property' | 'option_catalog';
  catalogId?: string;
  catalogName?: string;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface GraphEdge {
  id: string;
  source: string;
  target: string;
  edgeType: 'dependency' | 'option_catalog' | 'calculation';
  label?: string;
}

interface GlobalPropertyRelationshipGraphProps {
  propertyKeys?: string[]; // Optional: filter to specific properties
  /** When provided, skip N+1 getPropertyDefinition calls. */
  propertyDefinitions?: PropertyDefinition[];
  onPropertySelect?: (propertyKey: string) => void;
  height?: number;
}

type RelationshipFilter = 'all' | 'dependencies' | 'option_catalogs' | 'calculations';

export const GlobalPropertyRelationshipGraph: React.FC<GlobalPropertyRelationshipGraphProps> = ({
  propertyKeys,
  propertyDefinitions,
  onPropertySelect,
  height = 600
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [properties, setProperties] = useState<Map<string, PropertyDefinition>>(new Map());
  const [loading, setLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState<RelationshipFilter>('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPropertyKey, setSelectedPropertyKey] = useState<string | null>(null);

  // Load all properties and build graph
  const loadGraphData = useCallback(async () => {
    setLoading(true);
    try {
      const propertiesMap = new Map<string, PropertyDefinition>();

      if (propertyDefinitions && propertyDefinitions.length > 0) {
        for (const def of propertyDefinitions) {
          const key = def.property_key;
          if (key) propertiesMap.set(key, def);
        }
      } else if (propertyKeys && propertyKeys.length > 0) {
        for (const key of propertyKeys) {
          try {
            const response = await GlobalPropertiesService.getPropertyDefinition(key);
            propertiesMap.set(key, response.definition);
          } catch (error) {
            console.warn(`Failed to load property ${key}:`, error);
          }
        }
      }
      
      setProperties(propertiesMap);
    } catch (error: any) {
      console.error('Error loading property graph:', error);
      message.error('Failed to load property relationships');
    } finally {
      setLoading(false);
    }
  }, [propertyKeys, propertyDefinitions]);

  useEffect(() => {
    loadGraphData();
  }, [loadGraphData]);

  // Build graph nodes and edges from properties
  const { nodes, edges } = useMemo(() => {
    const graphNodes: GraphNode[] = [];
    const graphEdges: GraphEdge[] = [];
    const nodeMap = new Map<string, GraphNode>();
    const catalogNodes = new Map<string, GraphNode>();

    // Create property nodes
    properties.forEach((def, key) => {
      const node: GraphNode = {
        id: key,
        propertyKey: key,
        propertyType: def.property_type,
        nodeType: 'property'
      };
      graphNodes.push(node);
      nodeMap.set(key, node);

      // Create option catalog node if linked
      if (def.global_option_catalog_ref) {
        const catalogId = def.global_option_catalog_ref;
        if (!catalogNodes.has(catalogId)) {
          const catalogNode: GraphNode = {
            id: `catalog_${catalogId}`,
            propertyKey: `Catalog: ${catalogId.substring(0, 8)}...`,
            propertyType: 'option_catalog',
            nodeType: 'option_catalog',
            catalogId: catalogId
          };
          graphNodes.push(catalogNode);
          catalogNodes.set(catalogId, catalogNode);
        }

        // Create edge from property to catalog
        graphEdges.push({
          id: `${key}_catalog_${catalogId}`,
          source: key,
          target: `catalog_${catalogId}`,
          edgeType: 'option_catalog',
          label: 'uses catalog'
        });
      }

      // Create dependency / calculation edges from catalog shapes
      // (inputs[], nested source_properties, legacy top-level source_properties).
      const depKeys = dependencyKeysFromCalculationDependencies(
        def.calculation_dependencies as Record<string, unknown> | undefined
      );
      const hasFormula = Boolean(
        def.calculation_dependencies &&
          typeof (def.calculation_dependencies as { formula?: unknown }).formula === 'string'
      );
      for (const propertyKey of depKeys) {
        if (!properties.has(propertyKey)) continue;
        graphEdges.push({
          id: `${key}_dep_${propertyKey}`,
          source: propertyKey,
          target: key,
          edgeType: hasFormula ? 'calculation' : 'dependency',
          label:
            (def.calculation_dependencies as { type?: string } | undefined)?.type ||
            (hasFormula ? 'calculation' : 'dependency'),
        });
      }
    });

    return { nodes: graphNodes, edges: graphEdges };
  }, [properties]);

  // Filter nodes and edges
  const filteredData = useMemo(() => {
    let filteredNodes = nodes;
    let filteredEdges = edges;

    // Filter by search text
    if (searchText) {
      filteredNodes = filteredNodes.filter(node =>
        node.propertyKey.toLowerCase().includes(searchText.toLowerCase())
      );
      const nodeIds = new Set(filteredNodes.map(n => n.id));
      filteredEdges = filteredEdges.filter(edge =>
        nodeIds.has(edge.source) && nodeIds.has(edge.target)
      );
    }

    // Filter by relationship type
    if (relationshipFilter !== 'all') {
      filteredEdges = filteredEdges.filter(edge => {
        if (relationshipFilter === 'dependencies') return edge.edgeType === 'dependency';
        if (relationshipFilter === 'option_catalogs') return edge.edgeType === 'option_catalog';
        if (relationshipFilter === 'calculations') return edge.edgeType === 'calculation';
        return true;
      });
      const edgeNodeIds = new Set<string>();
      filteredEdges.forEach(edge => {
        edgeNodeIds.add(edge.source);
        edgeNodeIds.add(edge.target);
      });
      filteredNodes = filteredNodes.filter(node => edgeNodeIds.has(node.id));
    }

    return { nodes: filteredNodes, edges: filteredEdges };
  }, [nodes, edges, searchText, relationshipFilter]);

  // Render graph with D3
  useEffect(() => {
    if (!svgRef.current || loading || filteredData.nodes.length === 0) {
      // Clean up any existing zoom behavior
      if (svgRef.current) {
        d3.select(svgRef.current).on('.zoom', null);
      }
      return;
    }

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = containerRef.current?.clientWidth || 800;
    const margin = { top: 20, right: 20, bottom: 20, left: 20 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create force simulation
    const simulation = d3
      .forceSimulation(filteredData.nodes as any)
      .force(
        'link',
        d3
          .forceLink(filteredData.edges)
          .id((d: any) => d.id)
          .distance(150)
      )
      .force('charge', d3.forceManyBody().strength(-400))
      .force('center', d3.forceCenter(innerWidth / 2, innerHeight / 2))
      .force('collision', d3.forceCollide().radius(60));

    // Create container
    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create zoom behavior
    const zoomBehavior = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        if (event && event.transform) {
          g.attr('transform', event.transform.toString());
        }
      });

    svg.call(zoomBehavior as any);

    // Draw edges
    const link = g
      .append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(filteredData.edges)
      .enter()
      .append('line')
      .attr('stroke', (d) => {
        if (d.edgeType === 'option_catalog') return '#13c2c2';
        if (d.edgeType === 'dependency') return '#1890ff';
        return '#999';
      })
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 2);

    // Draw edge labels
    const edgeLabels = g
      .append('g')
      .attr('class', 'edge-labels')
      .selectAll('text')
      .data(filteredData.edges.filter(e => e.label))
      .enter()
      .append('text')
      .attr('class', 'edge-label')
      .text(d => d.label || '')
      .attr('font-size', '10px')
      .attr('fill', '#666');

    // Draw nodes
    const node = g
      .append('g')
      .attr('class', 'nodes')
      .selectAll('circle')
      .data(filteredData.nodes)
      .enter()
      .append('circle')
      .attr('r', (d) => (d.nodeType === 'option_catalog' ? 25 : 20))
      .attr('fill', (d) => {
        if (d.nodeType === 'option_catalog') return '#13c2c2';
        return '#1890ff';
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        if (d.nodeType === 'property') {
          setSelectedPropertyKey(d.propertyKey);
          setShowDetailsModal(true);
          onPropertySelect?.(d.propertyKey);
        }
      })
      .call(
        d3
          .drag<SVGCircleElement, GraphNode>()
          .on('start', dragstarted)
          .on('drag', dragged)
          .on('end', dragended) as any
      );

    // Draw node labels
    const nodeLabels = g
      .append('g')
      .attr('class', 'node-labels')
      .selectAll('text')
      .data(filteredData.nodes)
      .enter()
      .append('text')
      .attr('class', 'node-label')
      .text(d => d.propertyKey.length > 20 ? d.propertyKey.substring(0, 20) + '...' : d.propertyKey)
      .attr('font-size', '12px')
      .attr('fill', '#333')
      .attr('text-anchor', 'middle')
      .attr('dy', (d) => (d.nodeType === 'option_catalog' ? 40 : 35));

    // Update positions on simulation tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      edgeLabels
        .attr('x', (d: any) => (d.source.x + d.target.x) / 2)
        .attr('y', (d: any) => (d.source.y + d.target.y) / 2);

      node.attr('cx', (d: any) => d.x).attr('cy', (d: any) => d.y);
      nodeLabels.attr('x', (d: any) => d.x).attr('y', (d: any) => d.y);
    });

    function dragstarted(event: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event: any) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event: any) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    return () => {
      // Clean up zoom behavior
      svg.on('.zoom', null);
      // Stop simulation
      simulation.stop();
    };
  }, [filteredData, loading, height, onPropertySelect]);

  const handleExport = () => {
    if (!svgRef.current) return;
    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'global-properties-graph.svg';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Global Properties Relationship Graph</Title>
          <Tag color="blue">{filteredData.nodes.length} nodes</Tag>
          <Tag color="cyan">{filteredData.edges.length} edges</Tag>
        </Space>
      }
      extra={
        <Space>
          <Search
            placeholder="Search properties..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            value={relationshipFilter}
            onChange={setRelationshipFilter}
            style={{ width: 150 }}
          >
            <Option value="all">All Relationships</Option>
            <Option value="dependencies">Dependencies</Option>
            <Option value="option_catalogs">Option Catalogs</Option>
            <Option value="calculations">Calculations</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={loadGraphData}>Refresh</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>Export</Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <div ref={containerRef} style={{ width: '100%', height: `${height}px`, overflow: 'hidden' }}>
          <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
        </div>
        {filteredData.nodes.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">No properties to display. Load properties to see relationships.</Text>
          </div>
        )}
      </Spin>

      {showDetailsModal && selectedPropertyKey && (
        <GlobalPropertyDetailsModal
          propertyKey={selectedPropertyKey}
          visible={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedPropertyKey(null);
          }}
        />
      )}
    </Card>
  );
};

